/**
 * A per-save invented vocabulary. Every world coins its own words for the
 * handful of concepts the Song talks about, from its own little phoneme set —
 * so two saves sound like two different peoples, and the words a player learns
 * to recognize ("that's their word for water") are genuinely theirs.
 *
 * As fluency rises the Song "translates" more concepts into plain speech but
 * keeps a few core words native (YOU, WE, SONG), the way a culture keeps its
 * oldest words. Cosmetic + main-thread only, so Math.random is fine.
 */
import { Rng } from '../core/rng';

export const CONCEPTS = [
  'YOU', 'WE', 'FOOD', 'WATER', 'GROUND', 'SUN', 'DARK', 'PLAY', 'GROW', 'WATCH',
  'WARM', 'GOOD', 'MORE', 'HOME', 'SONG', 'HAND', 'SKY', 'SLEEP', 'FRIEND', 'BIG',
  'SMALL', 'SEE', 'COME', 'GONE', 'NEW', 'DREAM', 'NAME', 'FEAR', 'REMEMBER', 'MAKE',
] as const;
export type Concept = (typeof CONCEPTS)[number];

const ENGLISH: Record<Concept, string> = {
  YOU: 'you', WE: 'we', FOOD: 'food', WATER: 'water', GROUND: 'ground',
  SUN: 'sun', DARK: 'dark', PLAY: 'play', GROW: 'grow', WATCH: 'watch',
  WARM: 'warm', GOOD: 'good', MORE: 'more', HOME: 'home', SONG: 'song',
  HAND: 'hand', SKY: 'sky', SLEEP: 'sleep', FRIEND: 'friend', BIG: 'big',
  SMALL: 'small', SEE: 'see', COME: 'come', GONE: 'gone', NEW: 'new',
  DREAM: 'dream', NAME: 'name', FEAR: 'fear', REMEMBER: 'remember', MAKE: 'make',
};

const CORE: Concept[] = ['YOU', 'WE', 'SONG'];

const CONSONANTS = 'kmtplnsbdgwvzrfhj'.split('');
const VOWELS = 'aeiou'.split('');

function shuffle<T>(arr: readonly T[], rng: Rng): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}

export class Conlang {
  private readonly words = new Map<Concept, string>();
  private readonly glyphOf = new Map<Concept, number>();

  constructor(seedString: string) {
    const rng = Rng.fromString('conlang:' + seedString);
    // this world's phoneme inventory — a small, distinctive subset
    const cons = shuffle(CONSONANTS, rng).slice(0, 6 + rng.int(3));
    const vows = shuffle(VOWELS, rng).slice(0, 3 + rng.int(2));
    const syl = (): string => (cons[rng.int(cons.length)] as string) + (vows[rng.int(vows.length)] as string);

    for (const concept of CONCEPTS) {
      let w: string;
      const shape = rng.next();
      if (shape < 0.28) {
        const s = syl();
        w = s + s; // reduplicated: mama, gigi
      } else if (shape < 0.72) {
        w = syl() + syl();
      } else {
        w = syl() + syl() + (rng.next() < 0.5 ? (cons[rng.int(cons.length)] as string) : syl());
      }
      this.words.set(concept, w);
      this.glyphOf.set(concept, (Math.abs(hash(concept)) % 24));
    }
  }

  /** The invented word for a concept (always native). */
  coined(concept: Concept): string {
    return this.words.get(concept) ?? concept.toLowerCase();
  }

  /** Plain-speech word for a concept. */
  english(concept: Concept): string {
    return ENGLISH[concept];
  }

  /** A stable glyph index (0..23) for this concept, so it always looks the same. */
  glyph(concept: Concept): number {
    return this.glyphOf.get(concept) ?? 0;
  }

  /**
   * Rendered word for a concept at a fluency stage: coined when young, drifting
   * to plain speech as the Song matures — but core words stay native longer.
   */
  word(concept: Concept, stage: number): { text: string; coined: boolean } {
    const core = CORE.includes(concept);
    const coinChance = core
      ? stage >= 4 ? 0.55 : 0.9
      : Math.max(0.05, 0.92 - stage * 0.24);
    if (Math.random() < coinChance) return { text: this.coined(concept), coined: true };
    return { text: this.english(concept), coined: false };
  }
}

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  return h | 0;
}
